import React, { useCallback } from 'react'
import { SyncStatusView } from '../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'
import { getIntegrationDisplayName } from '../../common/integrations'
import { IntegrationIcon } from '../../components/IntegrationIcon'
import { formatDate } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { getExternalLink } from '../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/utils'
import { useAppSelector } from '../../store/hooks'
import { selectCurrentUser } from '../../store/meetingSummary/user'
import { useTracking } from '../../apps/professional-app/fa/common/tracking/hooks'
import { TrackEvent } from '../../apps/professional-app/fa/common/tracking'
import { LinkButton } from '../../components/Button/LinkButton'
import { Button } from '../../components/Button/Button'
import { useCopyContactHistory } from './hooks/useCopyContactHistory'

interface ExternalUpdateHistoryHeaderProps {
  syncStatus: SyncStatusView
}

export const ExternalUpdateHistoryHeader: React.FC<ExternalUpdateHistoryHeaderProps> = ({ syncStatus }) => {
  const currentUser = useAppSelector(selectCurrentUser)
  const { trackingClient } = useTracking()
  const { handleCopyAll, isCopyEnabled } = useCopyContactHistory()

  const contactName = syncStatus.name || 'Contact'
  const contactObjectType = syncStatus.externalContactObjectType || 'contact'
  const serviceName = getIntegrationDisplayName(syncStatus.serviceId)
  const formattedDate = formatDate(syncStatus.syncedAt)
  const externalLink = getExternalLink({ syncStatus, currentUser })

  const onClickOpenExternalLink = useCallback(
    () =>
      trackingClient.track({
        eventName: TrackEvent.CRM_LINK_CLICKED,
        eventDetails: {
          serviceId: syncStatus.serviceId,
          source: 'changelog',
        },
      }),
    [trackingClient, syncStatus.serviceId],
  )

  return (
    <div className='p-2 border-bottom'>
      <h3 className='mb-3'>
        '{contactName}' {contactObjectType} saved in {serviceName}
      </h3>
      <div className='d-flex align-items-center justify-content-between'>
        <div className='d-flex align-items-center gap-3'>
          <div className='d-flex align-items-center gap-2'>
            <IntegrationIcon serviceId={syncStatus.serviceId} width='1rem' height='1rem' />
            <span>{serviceName}</span>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <i className='bi bi-calendar' />
            <span>{formattedDate}</span>
          </div>
        </div>
        <div className='d-flex align-items-center gap-2'>
          {isCopyEnabled && (
            <Button onClick={handleCopyAll} variant='grayDarkGhostButton' className='button-medium'>
              <i className='bi bi-clipboard me-2' />
              Copy all
            </Button>
          )}
          {externalLink && (
            <LinkButton
              onClick={onClickOpenExternalLink}
              variant='grayDarkGhostButton'
              className='button-medium'
              relative='route'
              target='_blank'
              to={externalLink}
            >
              <i className='bi bi-box-arrow-up-right me-2' />
              Open in {serviceName}
            </LinkButton>
          )}
        </div>
      </div>
    </div>
  )
}
